import { z } from "zod";

/**
 * Project files Zot and agents can retrieve, as the Artifacts section tables them: documents,
 * data, and images. Every kind extracts server-side text and feeds retrieval with citations;
 * files with no extractable text fail honestly instead of indexing silently.
 */
export const projectFileKinds = ["pdf", "markdown", "word", "yaml", "csv", "image"] as const;
export const projectFileKindSchema = z.enum(projectFileKinds);
export type ProjectFileKind = z.infer<typeof projectFileKindSchema>;

/** How far a file has travelled towards retrieval. */
export const projectFileIndexStates = ["indexing", "ready", "unindexed", "failed"] as const;
export const projectFileIndexStateSchema = z.enum(projectFileIndexStates);
export type ProjectFileIndexState = z.infer<typeof projectFileIndexStateSchema>;

/** The largest upload the API accepts, in bytes. Larger files answer 413 with this limit. */
export const PROJECT_FILE_MAX_BYTES = 10 * 1024 * 1024;
/** The largest extracted text the API accepts, in characters. Longer text answers 413. */
export const PROJECT_FILE_MAX_TEXT = 200_000;

/** MIME types the server accepts per kind. Browser MIME values are never trusted. */
export const projectFileKindMimeTypes: Record<ProjectFileKind, readonly string[]> = {
  pdf: ["application/pdf"],
  markdown: ["text/markdown", "text/plain", "text/x-markdown"],
  word: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  yaml: ["text/yaml", "application/yaml", "application/x-yaml", "text/plain"],
  csv: ["text/csv", "application/csv", "text/plain"],
  image: ["image/png", "image/jpeg"],
};

/** File extensions the server accepts per kind, checked against the file name. */
export const projectFileKindExtensions: Record<ProjectFileKind, readonly string[]> = {
  pdf: [".pdf"],
  markdown: [".md", ".markdown"],
  word: [".docx", ".doc"],
  yaml: [".yaml", ".yml"],
  csv: [".csv"],
  image: [".png", ".jpg", ".jpeg"],
};

const fileNameSchema = z
  .string()
  .trim()
  .min(1, "Name the file.")
  .max(255)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9 _.'()-]*\.[A-Za-z0-9]{1,10}$/,
    "The name needs an extension, for example brief-2026-03-05.md.",
  );

/** One stored project file: metadata always, redacted extracted text once indexed. */
export const projectFileSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1).max(255),
  kind: projectFileKindSchema,
  mimeType: z.string().min(1).max(127),
  sizeBytes: z.number().int().min(1),
  digest: z.string().min(1).max(128),
  indexState: projectFileIndexStateSchema,
  indexError: z.string().nullable(),
  uploaderName: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type ProjectFile = z.infer<typeof projectFileSchema>;

export const projectFileListSchema = z.array(projectFileSchema);
export type ProjectFileList = z.infer<typeof projectFileListSchema>;

/**
 * `POST .../files`: stores one file. The browser reads the file bytes and sends them as
 * base64; the server validates kind, extension, MIME, and signature, extracts the plain text
 * itself, and redacts before storing. `text` is legacy client input and is ignored.
 */
export const uploadProjectFileSchema = z.object({
  name: fileNameSchema,
  kind: projectFileKindSchema,
  mimeType: z.string().trim().min(1).max(127),
  sizeBytes: z.number().int().min(1).max(PROJECT_FILE_MAX_BYTES),
  contentBase64: z
    .string()
    .max(15 * 1024 * 1024)
    .optional()
    .default(""),
  text: z.string().max(PROJECT_FILE_MAX_TEXT).optional().default(""),
});
export type UploadProjectFile = z.infer<typeof uploadProjectFileSchema>;
